import { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { MessageBalloon } from "../../../MessageBalloon/Index";
import { translateAnimalCategoryDescription } from "../../../../i18n/translateAnimalCategory";
import { ReactComponent as PointIcon } from "../../../../assets/svg/icons/point.svg";
import { ReactComponent as BackIcon } from "../../../../assets/svg/icons/back.svg";
import { ReactComponent as AnimalsIcon } from "../../../../assets/svg/icons/animais.svg";
import { ReactComponent as AreaIcon } from "../../../../assets/svg/icons/areaIcon.svg";
import { ReactComponent as GrassIcon } from "../../../../assets/svg/icons/grass.svg";
import { ButtonPressed } from "../../../Buttons/ButtonPressed";
import { ButtonYesOrNo } from "../../../Buttons/ButtonYesOrNo";

interface AnimalReceiveProps {
  task: ITaskAnimalTransfer | undefined;
}

export default function InfoAnimalReceive(task: AnimalReceiveProps) {
  const [taskAnimalReceive, setTaskAnimalReceive] =
    useState<ITaskAnimalTransfer>();
  const hasSkipReason = taskAnimalReceive?.skip_reason != null;

  useEffect(() => {
    setTaskAnimalReceive(task.task);
  }, [task.task]);

  const initialTransferCategories =
    taskAnimalReceive?.categories?.filter(
      (category) => category.task_type === "Início de Transferência",
    ) ?? [];

  const completionTransferCategories =
    taskAnimalReceive?.categories?.filter(
      (category) => category.task_type === "Conclusão de Transferência",
    ) ?? [];

  const getCategoryKey = (category: Category) =>
    [
      category.category_description.id,
      category.category_description.type,
      category.category_description.name,
      category.category_description.age_description,
    ].join("-");

  const getQuantityByCategory = (categories: Category[]) =>
    categories.reduce<Record<string, number>>((acc, category) => {
      const categoryKey = getCategoryKey(category);

      acc[categoryKey] =
        (acc[categoryKey] ?? 0) + Number(category.quantity ?? 0);

      return acc;
    }, {});

  const initialQuantitiesByCategory = getQuantityByCategory(
    initialTransferCategories,
  );
  const completionQuantitiesByCategory = getQuantityByCategory(
    completionTransferCategories,
  );
  const transferCategoryKeys = Array.from(
    new Set([
      ...Object.keys(initialQuantitiesByCategory),
      ...Object.keys(completionQuantitiesByCategory),
    ]),
  );
  const hasQuantityDifference = transferCategoryKeys.some(
    (categoryKey) =>
      initialQuantitiesByCategory[categoryKey] !==
      completionQuantitiesByCategory[categoryKey],
  );

  const receivedOptionSelected =
    hasQuantityDifference || hasSkipReason ? "no" : "yes";

  return (
    <>
      <div>
        <span><Trans i18nKey="taskDetails.fromArea" /></span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <AreaIcon width={30} className="iconInitialTaskInfo" />
          <div className="labelInitialTaskInfo">
            <span>{taskAnimalReceive?.source_area_full_initials}</span>
            <PointIcon width={15} />
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <MessageBalloon
          text={`Você recebeu esses animais com essas especificações?`}
          pointed
        />

        {!hasSkipReason &&
          initialTransferCategories.map((category, index) => (
            <div style={{ display: "flex", gap: 10 }} key={index}>
              <MessageBalloon text={`${category.quantity}`} />
              <MessageBalloon
                text={translateAnimalCategoryDescription(category.category_description)}
              />
            </div>
          ))}

        <ButtonYesOrNo onlyResult optionSelected={receivedOptionSelected} />

        {hasSkipReason ? (
          <MessageBalloon text={`${taskAnimalReceive?.skip_reason}`} answer />
        ) : hasQuantityDifference ? (
          <>
            <MessageBalloon text={`Quantidade de animais recebidos`} pointed />
            {completionTransferCategories.map((category, index) => (
              <div style={{ display: "flex", gap: 10 }} key={index}>
                <MessageBalloon text={`${category.quantity}`} />
                <MessageBalloon
                  text={translateAnimalCategoryDescription(category.category_description)}
                />
              </div>
            ))}
          </>
        ) : null}
      </div>

      {!hasSkipReason && (
        <>
          <div className="containerQuestion">
            <MessageBalloon text={`Deseja criar um novo lote?`} pointed />
            <ButtonYesOrNo
              onlyResult
              optionSelected={taskAnimalReceive?.is_new_batch ? "yes" : "no"}
            />
          </div>

          <div hidden={!taskAnimalReceive?.is_new_batch}>
            <span><Trans i18nKey="taskDetails.onBatch" /></span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <AnimalsIcon width={30} className="iconInitialTaskInfo" />
              <div className="labelInitialTaskInfo">
                <span>{taskAnimalReceive?.batch_initials}</span>
                <PointIcon width={15} />
              </div>
            </div>
            <MessageBalloon
              text={`O novo lote se chamará ${taskAnimalReceive?.batch_initials}`}
              pointed
            />
          </div>

          <div hidden={!!taskAnimalReceive?.is_new_batch}>
            <span><Trans i18nKey="taskDetails.onBatch" /></span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <AnimalsIcon width={30} className="iconInitialTaskInfo" />
              <div className="labelInitialTaskInfo">
                <span>{taskAnimalReceive?.batch_initials}</span>
                <PointIcon width={15} />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
